import React, { useState, useEffect } from "react";
import { Transaction, BankAccount } from "../types";
import { transactionsAPI, accountsAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<Transaction | null>(null);
  const [filters, setFilters] = useState({
    account: "",
    type: "",
    category: "",
    startDate: "",
    endDate: "",
  });
  const [formData, setFormData] = useState({
    account: "",
    type: "credit" as "credit" | "debit",
    amount: "",
    description: "",
    category: "Personal" as "Personal" | "Client",
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTransactions();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const [transactionsResponse, accountsData] = await Promise.all([
        transactionsAPI.getTransactions(),
        accountsAPI.getAccounts(),
      ]);
      setTransactions(transactionsResponse.transactions);
      setAccounts(accountsData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== ""),
      );
      const transactionsResponse =
        await transactionsAPI.getTransactions(params);
      setTransactions(transactionsResponse.transactions);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load transactions");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.account || !formData.type || !formData.amount || !formData.description) {
      setError("All required fields must be filled");
      return;
    }
    
    // Validate amount
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0 || !isFinite(amount)) {
      setError("Please enter a valid positive amount");
      return;
    }
    
    try {
      setError(""); // Clear any previous errors
      const data = {
        account: formData.account,
        type: formData.type,
        amount: amount,
        description: formData.description.trim(),
        category: formData.category,
        date: formData.date,
      };

      if (editingTransaction) {
        await transactionsAPI.updateTransaction(editingTransaction._id, data);
      } else {
        await transactionsAPI.createTransaction(data);
      }

      setFormData({
        account: "",
        type: "credit",
        amount: "",
        description: "",
        category: "Personal",
        date: new Date().toISOString().split("T")[0],
      });
      setShowAddForm(false);
      setEditingTransaction(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save transaction");
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      account:
        typeof transaction.account === "string"
          ? transaction.account
          : transaction.account._id,
      type: transaction.type,
      amount: transaction.amount.toString(),
      description: transaction.description,
      category: transaction.category,
      date: transaction.date.split("T")[0],
    });
    setShowAddForm(true);
  };

  const handleDelete = async (transactionId: string) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        await transactionsAPI.deleteTransaction(transactionId);
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete transaction");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      account: "",
      type: "credit",
      amount: "",
      description: "",
      category: "Personal",
      date: new Date().toISOString().split("T")[0],
    });
    setShowAddForm(false);
    setEditingTransaction(null);
  };

  const getAccountName = (transaction: Transaction) => {
    if (!transaction.account) return "Unknown Account";
    if (typeof transaction.account === "string") {
      const account = accounts.find((acc) => acc._id === transaction.account);
      if (!account) return "Unknown Account";
      return `${account.bankName} (***${(account.accountNumber || "").slice(-4)})`;
    }
    if (!transaction.account.bankName || !transaction.account.accountNumber)
      return "Unknown Account";
    return `${transaction.account.bankName} (***${transaction.account.accountNumber.slice(-4)})`;
  };

  if (loading && initialLoad) {
    return (
      <PageTransition>
        {/* Mobile top bar for sidebar toggle (assumes DashboardLayout or similar) */}
        <div className="md:hidden sticky top-0 z-30 bg-white flex items-center h-14 shadow px-4 mb-4">
          <button
            type="button"
            className="mr-3 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
            onClick={() => {
              // Custom event for DashboardLayout to open sidebar
              const event = new CustomEvent("openSidebar");
              window.dispatchEvent(event);
            }}
            aria-label="Open sidebar menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <span className="text-lg font-bold text-red-500">Transactions</span>
        </div>
        <div className="p-4 sm:p-6 max-w-4xl mx-auto w-full">
          {/* Header skeleton */}
          <div className="mb-8 flex justify-between items-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded w-36 animate-pulse"></div>
          </div>

          {/* Filters skeleton */}
          <div className="mb-6 bg-white shadow-sm rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>

          <LoadingSkeleton variant="table" count={5} />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
            <p className="text-gray-600">
              Track all your financial transactions
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-500 hover:bg-red-600 transition-colors shadow-sm w-full sm:w-auto justify-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add Transaction
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <svg
              className="w-4 h-4 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
            <button
              onClick={() => setError("")}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white shadow-sm rounded-lg border border-gray-200 p-4 overflow-x-auto">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Filters</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <select
                value={filters.account}
                onChange={(e) =>
                  setFilters({ ...filters, account: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Accounts</option>
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.bankName} (***{account.accountNumber.slice(-4)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Types</option>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </div>
            <div>
              <select
                value={filters.category}
                onChange={(e) =>
                  setFilters({ ...filters, category: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Categories</option>
                <option value="Personal">Personal</option>
                <option value="Client">Client</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Start Date"
              />
            </div>
            <div>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="End Date"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() =>
                setFilters({
                  account: "",
                  type: "",
                  category: "",
                  startDate: "",
                  endDate: "",
                })
              }
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Add/Edit Transaction Form */}
        {showAddForm && (
          <div className="mb-8 bg-white shadow-sm rounded-lg border border-gray-200 p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTransaction ? "Edit Transaction" : "Add New Transaction"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account *
                  </label>
                  <select
                    required
                    value={formData.account}
                    onChange={(e) =>
                      setFormData({ ...formData, account: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Select account</option>
                    {accounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.bankName} (***{account.accountNumber.slice(-4)}
                        )
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type: e.target.value as "credit" | "debit",
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="credit">Credit (Money In)</option>
                    <option value="debit">Debit (Money Out)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={formData.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string or valid positive numbers
                      if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) > 0)) {
                        setFormData({ ...formData, amount: value });
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setFormData({ ...formData, amount: value.toFixed(2) });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category: e.target.value as "Personal" | "Client",
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="Personal">Personal</option>
                    <option value="Client">Client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Transaction description"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-500 hover:bg-red-600 transition-colors"
                >
                  {editingTransaction
                    ? "Update Transaction"
                    : "Add Transaction"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <div className="text-center py-12 bg-white shadow-sm rounded-lg border border-gray-200">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No transactions yet
            </h3>
            <p className="text-gray-500 mb-4">
              Start by adding your first transaction
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              Add Transaction
            </button>
          </div>
        ) : (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Transaction History ({transactions.length}{" "}
                {transactions.length === 1 ? "transaction" : "transactions"})
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {[...transactions]
                .sort((a, b) => {
                  const dateA = new Date(a.date).getTime();
                  const dateB = new Date(b.date).getTime();
                  if (dateB !== dateA) {
                    return dateB - dateA;
                  }
                  // If same date, sort by createdAt
                  const createdA = a.createdAt
                    ? new Date(a.createdAt).getTime()
                    : 0;
                  const createdB = b.createdAt
                    ? new Date(b.createdAt).getTime()
                    : 0;
                  return createdB - createdA;
                })
                .map((transaction) => (
                  <div
                    key={transaction._id}
                    className="p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              transaction.type === "credit"
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 text-left">
                              {transaction.description}
                            </p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                              <span>{getAccountName(transaction)}</span>
                              <span>
                                {transaction.date &&
                                !isNaN(new Date(transaction.date).getTime())
                                  ? new Date(
                                      transaction.date,
                                    ).toLocaleDateString()
                                  : "Invalid Date"}
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  transaction.category === "Personal"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-purple-100 text-purple-800"
                                }`}
                              >
                                {transaction.category || "Unknown"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div
                            className={`text-lg font-semibold ${
                              transaction.type === "credit"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {transaction.type === "credit" ? "+" : "-"}₹
                            {transaction.amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {transaction.type === "credit" ? "Credit" : "Debit"}
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Edit transaction"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(transaction._id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete transaction"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default Transactions;
